/**
 * Sentry initialisation for the Phoenix Suite admin frontend.
 *
 * Required env var (inlined at build time by Vite — anything not prefixed
 * with VITE_ is not exposed to the bundle):
 *   VITE_SENTRY_DSN
 *
 * Optional:
 *   VITE_SENTRY_ENVIRONMENT (default: import.meta.env.MODE)
 *   VITE_SENTRY_RELEASE (e.g. git SHA)
 *
 * If the DSN is missing, Sentry is a no-op (safe for local dev).
 */
import * as Sentry from '@sentry/react';

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE || 'development',
    release: import.meta.env.VITE_SENTRY_RELEASE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,         // see everything visible on screen...
        maskAllInputs: true,        // ...except inputs (likely PII)
        blockAllMedia: false,
      }),
    ],
    tracesSampleRate: 0.1,                // 10% of page-loads/navigations traced
    replaysSessionSampleRate: 0.05,       // 5% of sessions recorded
    replaysOnErrorSampleRate: 1.0,        // 100% of sessions with an error recorded
    sendDefaultPii: false,
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',
      'Network Error',                    // axios when network drops — expected sometimes
      'Request failed with status code 401',
      'Request failed with status code 403',
    ],
    beforeSend(event) {
      try {
        if (event.request?.headers) {
          delete event.request.headers.authorization;
          delete event.request.headers.Authorization;
        }
        // Don't send localStorage/cookies that may contain tokens
        if (event.request?.cookies) delete event.request.cookies;
      } catch { /* swallow */ }
      return event;
    },
  });
}

export { Sentry };
